import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FileDown, Loader2, TriangleAlert, Crown, Lock, LockOpen } from 'lucide-react';
import * as XLSX from 'xlsx';
import ReturnSummaryViewer, { ReturnSummaryViewerMeta } from '../components/ReturnSummaryViewer/ReturnSummaryViewer';
import { PortalJob } from '../types/portal';
import { fetchPortalJobs } from '../services/portalData';
import { submitPrintBatch, downloadPrintFile, uploadFileToSafeSend, saveFilesToMFiles, PrintFile, MFilesUploadResult, calcTitle, calcK1Title, parseReturnLetters } from '../services/printReturn';
import { fetchKnuulaContract } from '../services/knuula';
import { fetchReturnLockStatus, fetchReturnInUse, ReturnInUseStatus } from '../services/returnSummary';

function coerceObject(value: unknown): Record<string, string> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, string>;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, string>;
    } catch { /* fall through */ }
  }
  return {};
}

function normalize(s: unknown): string {
  return (s || '').toString().toLowerCase().trim();
}

function isVip(job: PortalJob): boolean {
  return (job.isVIP || '').toString().toUpperCase() === 'TRUE';
}

function missingAlert(job: PortalJob): string | null {
  const msel = (job.MSEL || '').trim().toUpperCase() === 'MISSING';
  const sow  = (job.SOW  || '').trim().toUpperCase() === 'MISSING';
  if (msel && sow) return 'Missing MSEL and SOW';
  if (msel) return 'Missing MSEL';
  if (sow)  return 'Missing SOW';
  return null;
}

function friendlyFileName(name: string): string {
  if (/acct/i.test(name)) return 'Accountant Copy';
  if (/govt/i.test(name)) return 'Government Copy';
  if (/clnt/i.test(name)) return 'Client Copy';
  return name;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(2)} MB`;
  if (bytes >= 1024)      return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

type PrintPhase =
  | 'pick-type' | 'loading' | 'pick-destination' | 'pick-files'
  | 'downloading' | 'uploading-safesend' | 'safesend-success'
  | 'saving-mfiles' | 'mfiles-success' | 'parsing-letters' | 'letters-ready' | 'done' | 'error';

type MFilesResult = { title: string; objId?: number; status: 'pending' | 'done' | 'error'; error?: string };
type LocationState = { from?: string; job?: PortalJob };

const INITIAL_FILES_SHOW = 10;

export default function JobDetail() {
  const { returnId: returnIdParam } = useParams();
  const decodedReturnId = useMemo(() => {
    try { return decodeURIComponent(returnIdParam || ''); }
    catch { return returnIdParam || ''; }
  }, [returnIdParam]);

  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const [job, setJob]               = useState<PortalJob | null>(state.job || null);
  const [jobLoading, setJobLoading] = useState(false);

  // Print modal
  const [printModalOpen, setPrintModalOpen]       = useState(false);
  const [printPhase, setPrintPhase]               = useState<PrintPhase>('pick-type');
  const [printMode, setPrintMode]                 = useState<'return' | 'k1s' | 'safesend' | 'letters'>('return');
  const [printDestination, setPrintDestination]   = useState<'local' | 'safesend' | 'mfiles'>('local');
  const [printBatchGuid, setPrintBatchGuid]       = useState('');
  const [printFiles, setPrintFiles]               = useState<PrintFile[]>([]);
  const [selectedFiles, setSelectedFiles]         = useState<Set<string>>(new Set());
  const [filesExpanded, setFilesExpanded]         = useState(false);
  const [downloadProgress, setDownloadProgress]   = useState<Record<string, 'pending' | 'done' | 'error'>>({});
  const [printError, setPrintError]               = useState('');
  const [safeSendReviewUrl, setSafeSendReviewUrl] = useState('');
  const [mfilesResults, setMfilesResults]         = useState<MFilesResult[]>([]);
  const [lettersBlob, setLettersBlob]             = useState<Blob | null>(null);
  const abortRef                                  = useRef(false);

  // Knuula
  const [knuulaLoading, setKnuulaLoading]     = useState(false);
  const [knuulaModalOpen, setKnuulaModalOpen] = useState(false);

  // Lock status
  const [lockStatus, setLockStatus]       = useState<boolean | null>(null);
  const [lockStatusLoading, setLockStatusLoading] = useState(false);

  // In-use status
  const [inUseStatus, setInUseStatus] = useState<ReturnInUseStatus | null>(null);

  const [meta, setMeta] = useState<ReturnSummaryViewerMeta>({
    header: { ClientID: '', TaxYear: '', ReturnType: '', EINorSSN: '', OfficeName: '', BusinessUnitName: '', TaxpayerName: '' },
    returnCount: 0,
  });

  useEffect(() => {
    let cancelled = false;
    async function loadJob() {
      if (job || !decodedReturnId) return;
      setJobLoading(true);
      try {
        const jobs = await fetchPortalJobs();
        if (cancelled) return;
        setJob(jobs.find((j) => (j.returnId || '') === decodedReturnId) || null);
      } finally {
        if (!cancelled) setJobLoading(false);
      }
    }
    loadJob();
    return () => { cancelled = true; };
  }, [decodedReturnId, job]);

  // True only when the returnId contains a return type letter (e.g. "2025P:...")
  // Jobs without a return type cannot use any CCH API (print, lock, summary).
  const hasReturnType = useMemo(() => /^\d{4}[A-Z]/.test(decodedReturnId || ''), [decodedReturnId]);

  useEffect(() => {
    if (!decodedReturnId || !hasReturnType) return;
    let cancelled = false;
    setLockStatusLoading(true);
    fetchReturnLockStatus(decodedReturnId)
      .then((locked) => { if (!cancelled) setLockStatus(locked); })
      .catch(() => { if (!cancelled) setLockStatus(null); })
      .finally(() => { if (!cancelled) setLockStatusLoading(false); });
    return () => { cancelled = true; };
  }, [decodedReturnId, hasReturnType]);

  useEffect(() => {
    if (!decodedReturnId || !hasReturnType) return;
    let cancelled = false;
    fetchReturnInUse(decodedReturnId)
      .then((s) => { if (!cancelled) setInUseStatus(s); })
      .catch(() => { if (!cancelled) setInUseStatus(null); });
    return () => { cancelled = true; };
  }, [decodedReturnId, hasReturnType]);

  const backTo = state.from || '/portal/jobs';

  // ── Replace the existing `fields` useMemo ────────────────────────────────
  const fields = useMemo(() => {
    if (!job) return [] as Array<[string, string]>;
    const emails = [
      job.PrimaryEmail ? `${job.PrimaryEmail} (primary)` : null,
      job.SpouseEmail  ? `${job.SpouseEmail} (spouse)`   : null,
    ].filter(Boolean).join(' • ');
    const addrParts = [job.Street, job.City, job.State, job.ZIP].filter(Boolean);
    const address = addrParts.length ? addrParts.join(', ') : '—';
    const phones = [
      job.Phone ? `${job.Phone} (office)` : null,
      job.Cell  ? `${job.Cell} (cell)`    : null,
    ].filter(Boolean).join(' • ');
    return [
      ['Client Partner',   job.ClientPartner || '—'],
      ['Client Manager',   job.ClientManager || '—'],
      ['Job Partner',      job.JobPartner    || '—'],
      ['Job Manager',      job.JobManager    || '—'],
      ['Current Staff',    job.CurrentStaff  || '—'],
      ['Office',           job.ClientOffice  || '—'],
      ['Extension',        job.ExtensionStatusFed || '—'],
      ['Work Status',      job.JobWorkStatus || '—'],
      ['Fed Jurisdiction', job.FedJurisdictionStatus || '—'],
      ['Email',            emails  || '—'],
      ['Address',          address],
      ['Phone',            phones  || '—'],
    ] as Array<[string, string]>;
    // NOTE: assembly_shipping is rendered separately below as a fixed row
  }, [job]);

  // ── Parse assembly_shipping into fixed 4-field layout + overflow ─────────
  const FIXED_ASM_KEYS = new Set([
    'Shipping Instructions',
    'Additional Comments (Shipping)',
    'Assembly Instructions',
    'Additional Comments (Assembly)',
  ]);

  const asmShip = useMemo(() => {
    const raw = coerceObject(job?.assembly_shipping);
    const extra = Object.entries(raw)
      .filter(([k]) => !FIXED_ASM_KEYS.has(k.trim()))
      .map(([k, v]) => [k.trim(), v || '—'] as [string, string]);
    return {
      shippingInstructions:       raw['Shipping Instructions']           || '—',
      additionalCommentsShipping: raw['Additional Comments (Shipping)']  || '—',
      assemblyInstructions:       raw['Assembly Instructions']           || '—',
      additionalCommentsAssembly: raw['Additional Comments (Assembly)']  || '—',
      extra,
    };
  }, [job]);

  // ── Excel summary download ────────────────────────────────────────────────
  function downloadMFilesExcel() {
    const rows = mfilesResults.map((r) => ({
      'M-Files Title':     r.title,
      'M-Files Object ID': r.objId ?? '',
      'Status':            r.status === 'done' ? 'Saved' : 'Error',
      'Error':             r.error ?? '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
      { wch: 50 }, // M-Files Title
      { wch: 20 }, // Object ID
      { wch: 10 }, // Status
      { wch: 40 }, // Error
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'M-Files Summary');

    const clientCode = job?.ClientCode || 'unknown';
    const jobName    = (job?.Job_Name  || 'mfiles').replace(/[/\\?%*:|"<>]/g, '-');
    XLSX.writeFile(wb, `${clientCode} ${jobName} M-Files Summary.xlsx`);
  }

  // ── Parse return letters ──────────────────────────────────────────────────
  async function handleParseLetters() {
    if (!printBatchGuid || !printFiles.length || !job) return;
    // Letters mode uses the single client-copy file (first file in the batch)
    const file = printFiles[0];
    setPrintPhase('parsing-letters');
    try {
      const blob = await parseReturnLetters(printBatchGuid, file.BatchItemGuid, file.FileName);
      setLettersBlob(blob);
      setPrintPhase('letters-ready');
    } catch (err: any) {
      console.error('[parseReturnLetters] error:', err.message, err);
      setPrintError(err.message || String(err));
      setPrintPhase('error');
    }
  }

  function downloadLetters() {
    if (!lettersBlob || !job) return;
    const dotCode  = (job.ClientCode || '').replace('-', '.');
    // Extract tax year from returnId e.g. "2025S:04228.000:V1"
    const taxYear  = (job.returnId || '').match(/^(\d{4})/)?.[1] || '';
    const name     = `${dotCode} ${taxYear} Return Letters Filing Instructions.pdf`;
    const url = URL.createObjectURL(lettersBlob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Knuula ────────────────────────────────────────────────────────────────
  async function handleKnuulaClick() {
    if (!job?.Job_Idx) return;
    setKnuulaLoading(true);
    try {
      const result = await fetchKnuulaContract(job.Job_Idx);
      if (result.found) window.open(result.url, '_blank', 'noopener,noreferrer');
      else setKnuulaModalOpen(true);
    } catch {
      setKnuulaModalOpen(true);
    } finally {
      setKnuulaLoading(false);
    }
  }

  // ── Print modal ───────────────────────────────────────────────────────────
  function openPrintModal() {
    abortRef.current = false;
    setPrintPhase('pick-type');
    setPrintMode('return');
    setPrintDestination('local');
    setPrintBatchGuid('');
    setPrintFiles([]);
    setSelectedFiles(new Set());
    setFilesExpanded(false);
    setDownloadProgress({});
    setPrintError('');
    setSafeSendReviewUrl('');
    setMfilesResults([]);
    setLettersBlob(null);
    setPrintModalOpen(true);
  }

  function closePrintModal() {
    abortRef.current = true;
    setPrintModalOpen(false);
  }

  async function handleSubmitPrint() {
    if (!job?.returnId) return;
    setPrintPhase('loading');
    setPrintError('');
    try {
      const inUse = await fetchReturnInUse(job.returnId);
      setInUseStatus(inUse);
      if (inUse.inUse) {
        const who = inUse.lastUpdatedBy || 'another user';
        setPrintError(`The return is currently in use by ${who}. Please try again when they have closed the return.`);
        setPrintPhase('error');
        return;
      }
    } catch { /* non-fatal — proceed and let CCH surface the real error */ }
    try {
      const data = await submitPrintBatch(job.returnId, printMode === 'letters' ? 'return' : printMode);
      setPrintBatchGuid(data.batchGuid);
      setPrintFiles(data.files);
      // Letters mode: skip file selection — parse immediately using the client copy file
      if (printMode === 'letters') {
        // Find the client copy file, or fall back to first file
        const clientFile = data.files.find((f) => /clnt/i.test(f.FileName)) || data.files[0];
        setPrintFiles([clientFile]);
        setPrintPhase('parsing-letters');
        try {
          const blob = await parseReturnLetters(data.batchGuid, clientFile.BatchItemGuid, clientFile.FileName);
          setLettersBlob(blob);
          setPrintPhase('letters-ready');
        } catch (err: any) {
          console.error('[parseReturnLetters] error:', err.message, err);
          setPrintError(err.message || String(err));
          setPrintPhase('error');
        }
        return;
      }
      // Default: select ALL files regardless of count
      setSelectedFiles(new Set(data.files.map((f) => f.FileName)));
      // Auto-expand if over threshold so user can see everything is selected
      if (data.files.length > INITIAL_FILES_SHOW) setFilesExpanded(true);
      setPrintPhase('pick-destination');
    } catch (err: any) {
      console.error('[printReturnBatch] error:', err.message, err);
      setPrintError(err.message || String(err));
      setPrintPhase('error');
    }
  }

  async function handleUploadToSafeSend() {
    const file = printFiles[0];
    if (!file || !printBatchGuid) return;
    setPrintPhase('uploading-safesend');
    try {
      const result = await uploadFileToSafeSend(printBatchGuid, file.BatchItemGuid, file.FileName, job?.ClientCode || '');
      setSafeSendReviewUrl(result.reviewUrl);
      setPrintPhase('safesend-success');
    } catch (err: any) {
      console.error('[uploadToSafeSend] error:', err.message, err);
      setPrintError(err.message || String(err));
      setPrintPhase('error');
    }
  }

  async function handleDownloadSelected() {
    if (!printBatchGuid || selectedFiles.size === 0) return;
    setPrintPhase('downloading');
    const initial: Record<string, 'pending' | 'done' | 'error'> = {};
    printFiles.forEach((f) => { if (selectedFiles.has(f.FileName)) initial[f.FileName] = 'pending'; });
    setDownloadProgress(initial);
    for (const file of printFiles) {
      if (!selectedFiles.has(file.FileName)) continue;
      if (abortRef.current) break;
      try {
        const blob = await downloadPrintFile(printBatchGuid, file.BatchItemGuid, file.FileName);
        const url = URL.createObjectURL(blob);
        const cleanName = printMode === 'safesend'
          ? (job?.ClientCode || '').replace('-', '.') + '.pdf'
          : printMode === 'k1s'
          ? calcK1Title(file.FileName) + '.pdf'
          : calcTitle(file.FileName, job?.ClientCode || '', job?.returnId || '') + '.pdf';
        const a = document.createElement('a');
        a.href = url; a.download = cleanName; a.click();
        URL.revokeObjectURL(url);
        setDownloadProgress((p) => ({ ...p, [file.FileName]: 'done' }));
      } catch {
        setDownloadProgress((p) => ({ ...p, [file.FileName]: 'error' }));
      }
    }
    setPrintPhase('done');
  }

  async function handleSaveToMFiles() {
    if (!printBatchGuid || selectedFiles.size === 0 || !job) return;
    setPrintPhase('saving-mfiles');
    const filesToSave = printFiles.filter((f) => selectedFiles.has(f.FileName));
    const initial: MFilesResult[] = filesToSave.map((f) => ({ title: f.FileName, status: 'pending' }));
    setMfilesResults(initial);
    try {
      const results = await saveFilesToMFiles(
        printBatchGuid,
        filesToSave.map((f) => ({ batchItemGuid: f.BatchItemGuid, fileName: f.FileName })),
        job.ClientName || '', job.ClientCode || '',
        job.Job_Name   || '', job.returnId   || '',
        printMode === 'k1s'
      );
      setMfilesResults(results.map((r: MFilesUploadResult) => ({
        title:  r.title,
        objId:  r.objId,
        status: 'done' as const,
      })));
    } catch (err: any) {
      console.error('[saveToMFiles] error:', err.message, err);
      setMfilesResults(filesToSave.map((f) => ({ title: f.FileName, status: 'error' as const, error: err.message })));
    }
    setPrintPhase('mfiles-success');
  }

  function toggleFile(fileName: string) {
    setSelectedFiles((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(fileName)) next.delete(fileName); else next.add(fileName);
      return next;
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="detail-grid">
      <div className="row" style={{ justifyContent: 'flex-start' }}>
        <button className="btn" type="button" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate(backTo); }}>
          ← Back
        </button>
        <span className="badge">returnId: {decodedReturnId || '—'}</span>
        <div style={{ marginLeft: 'auto' }}>
          {hasReturnType && lockStatusLoading && (
            <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.4)' }}>
              <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Checking lock…
            </span>
          )}
          {hasReturnType && !lockStatusLoading && lockStatus === true && (
            <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, backgroundColor: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', color: '#34d399' }}>
              <Lock size={13} /> Return Locked
            </span>
          )}
          {hasReturnType && !lockStatusLoading && lockStatus === false && (
            <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, backgroundColor: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.4)', color: '#f87171' }}>
              <LockOpen size={13} /> Return Not Locked (Prohibit Changes Unchecked)
            </span>
          )}
        </div>
      </div>

      {/* ── Knuula Modal ─────────────────────────────────────────────────── */}
      {knuulaModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setKnuulaModalOpen(false)}>
          <div className="jd-modal" style={{ backgroundColor: 'var(--jm-surface)', border: '1px solid var(--jm-line)', borderRadius: 10, padding: '28px 32px', minWidth: 360, maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 14 }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/KnuulaLogo.png" alt="Knuula" style={{ height: 20, width: 'auto' }} />
              <span style={{ fontWeight: 700, fontSize: 16 }}>No Contract Found</span>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>No Knuula contract was found for this job. Would you like to open the Knuula search window?</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn" type="button" style={{ flex: 1 }}
                onClick={() => { window.open('https://knuula.com/common-dashboard/parties', '_blank', 'noopener,noreferrer'); setKnuulaModalOpen(false); }}>
                Open Knuula Search
              </button>
              <button className="btn" type="button" style={{ opacity: 0.6 }} onClick={() => setKnuulaModalOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Print Modal ───────────────────────────────────────────────────── */}
      {printModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closePrintModal}>
          <div className="jd-modal" style={{ backgroundColor: 'var(--jm-surface)', border: '1px solid var(--jm-line)', borderRadius: 10, padding: '28px 32px', minWidth: 380, maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}
            onClick={(e) => e.stopPropagation()}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            {/* pick-type */}
            {printPhase === 'pick-type' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Print Options</div>
              <div className="muted" style={{ fontSize: 13 }}>Choose what to generate for <strong>{job?.Job_Name}</strong></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(['return', 'k1s', 'safesend', 'letters'] as const).map((m) => {
                  const k1Eligible = ['P', 'S', 'F'].includes((job?.EntityType || '').toUpperCase());
                  if (m === 'k1s' && !k1Eligible) return null;
                  return (
                    <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${printMode === m ? 'var(--jm-link)' : 'var(--jm-line)'}`, backgroundColor: printMode === m ? 'var(--jm-sel)' : 'transparent', transition: 'all 0.15s' }}>
                      <input type="radio" name="printMode" value={m} checked={printMode === m} onChange={() => setPrintMode(m as any)} style={{ accentColor: 'var(--jm-link)' }} />
                      <span style={{ fontWeight: 600 }}>
                        {m === 'return'  ? 'Print Return (Acct / Govt / Client copies)'
                        : m === 'k1s'   ? 'Print K-1s (one PDF per recipient)'
                        : m === 'safesend' ? 'Print SafeSend Copy (Client + K-1, single PDF)'
                        : 'Print Return Letters / Filing Instructions'}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn" type="button" onClick={handleSubmitPrint} style={{ flex: 1 }}><FileDown size={15} /> Generate</button>
                <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Cancel</button>
              </div>
            </>)}

            {/* loading */}
            {printPhase === 'loading' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Generating PDFs…</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--jm-dim)' }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Submitting batch and waiting for CCH to complete. This may take 30–60 seconds.
              </div>
              <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6, marginTop: 4 }}>Cancel</button>
            </>)}

            {/* pick-destination */}
            {printPhase === 'pick-destination' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Where should we send this?</div>
              <div className="muted" style={{ fontSize: 13 }}>Your PDFs are ready. Choose a destination.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn" type="button" style={{ justifyContent: 'flex-start', gap: 10, padding: '10px 14px' }}
                  onClick={() => { setPrintDestination('local'); setPrintPhase('pick-files'); }}>
                  <FileDown size={15} /> Save to local machine
                </button>
                {printMode === 'safesend' && (
                  <button className="btn" type="button" style={{ justifyContent: 'flex-start', gap: 10, padding: '10px 14px', borderColor: 'rgba(167,139,250,0.5)', color: 'var(--jm-purple)' }}
                    onClick={handleUploadToSafeSend}>
                    <span style={{ fontSize: 13 }}>☁</span> Upload to SafeSend
                  </button>
                )}
                {printMode !== 'safesend' && (
                  <button className="btn" type="button" style={{ justifyContent: 'flex-start', gap: 10, padding: '10px 14px', borderColor: 'rgba(103,232,249,0.5)', color: 'var(--jm-cyan)' }}
                    onClick={() => { setPrintDestination('mfiles'); setPrintPhase('pick-files'); }}>
                    <span style={{ fontSize: 13 }}>🗄</span> Save to M-Files
                  </button>
                )}
              </div>
              <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6, marginTop: 4 }}>Cancel</button>
            </>)}

            {/* uploading-safesend */}
            {printPhase === 'uploading-safesend' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Uploading to SafeSend…</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--jm-dim)' }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Logging in and uploading your return. This may take a moment.
              </div>
            </>)}

            {/* safesend-success */}
            {printPhase === 'safesend-success' && (<>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--jm-good)' }}>✓ Uploaded Successfully</div>
              <div className="muted" style={{ fontSize: 13 }}>The return has been uploaded to SafeSend and is ready for review.</div>
              <a href={safeSendReviewUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 16px', borderRadius: 6, textDecoration: 'none', backgroundColor: 'var(--jm-sel)', border: '1px solid var(--jm-link)', color: 'var(--jm-link)', fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                Open in SafeSend ↗
              </a>
              <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Close</button>
            </>)}

            {/* saving-mfiles */}
            {printPhase === 'saving-mfiles' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Saving to M-Files…</div>
              {mfilesResults.length > 4 && (
                <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, backgroundColor: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', color: 'var(--jm-warn)' }}>
                  ⚠ Saving {mfilesResults.length} files — this may take several minutes. Please keep this window open.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {mfilesResults.map((r: MFilesResult, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                    {r.status === 'pending' && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />}
                    {r.status === 'done'    && <span style={{ color: 'var(--jm-good)', flexShrink: 0 }}>✓</span>}
                    {r.status === 'error'   && <span style={{ color: 'var(--jm-bad)', flexShrink: 0 }}>✗</span>}
                    <span style={{ opacity: r.status === 'pending' ? 0.5 : 1 }}>{r.title}</span>
                  </div>
                ))}
              </div>
            </>)}

            {/* mfiles-success */}
            {printPhase === 'mfiles-success' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>M-Files Save Complete</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {mfilesResults.map((r: MFilesResult, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: r.status === 'done' ? 'var(--jm-good)' : 'var(--jm-bad)', flexShrink: 0 }}>{r.status === 'done' ? '✓' : '✗'}</span>
                    <span style={{ flex: 1 }}>{r.title}</span>
                    {r.objId != null && <span className="muted" style={{ fontSize: 11, flexShrink: 0 }}>ID {r.objId}</span>}
                    {r.status === 'error' && <span className="muted" style={{ fontSize: 11 }}>— {r.error}</span>}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn" type="button" onClick={downloadMFilesExcel}
                  style={{ flex: 1, borderColor: 'rgba(110,231,183,0.5)', color: 'var(--jm-good)' }}>
                  <FileDown size={14} /> Download Excel Summary
                </button>
                <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Close</button>
              </div>
            </>)}

            {/* parsing-letters */}
            {printPhase === 'parsing-letters' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Extracting Letters & Filing Instructions…</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--jm-dim)' }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Downloading return and extracting filing instruction pages. This may take a moment.
              </div>
              <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6, marginTop: 4 }}>Cancel</button>
            </>)}

            {/* letters-ready */}
            {printPhase === 'letters-ready' && (<>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--jm-good)' }}>✓ Letters Ready</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Filing instruction pages have been extracted from the return. Click below to download.
              </div>
              <button className="btn" type="button" onClick={downloadLetters}
                style={{ flex: 1, borderColor: 'rgba(110,231,183,0.5)', color: 'var(--jm-good)', marginTop: 4 }}>
                <FileDown size={14} /> Download Return Letters / Filing Instructions
              </button>
              <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Close</button>
            </>)}

            {/* pick-files */}
            {printPhase === 'pick-files' && (() => {
              const visibleFiles = filesExpanded ? printFiles : printFiles.slice(0, INITIAL_FILES_SHOW);
              const hiddenCount  = printFiles.length - INITIAL_FILES_SHOW;
              return (<>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Select Files</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {printFiles.length} file{printFiles.length !== 1 ? 's' : ''} ready — {selectedFiles.size} selected.{' '}
                  <button type="button" onClick={() => setSelectedFiles(new Set(printFiles.map((f) => f.FileName)))}
                    style={{ background: 'none', border: 'none', color: 'var(--jm-link)', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}>
                    Select all
                  </button>
                  {' · '}
                  <button type="button" onClick={() => setSelectedFiles(new Set())}
                    style={{ background: 'none', border: 'none', color: 'var(--jm-link)', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}>
                    Clear all
                  </button>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: 320, display: 'flex', flexDirection: 'column', gap: 5, paddingRight: 4 }}>
                  {visibleFiles.map((f) => (
                    <label key={f.FileName} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 10px', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                      border: `1px solid ${selectedFiles.has(f.FileName) ? 'var(--jm-link)' : 'var(--jm-line)'}`,
                      backgroundColor: selectedFiles.has(f.FileName) ? 'var(--jm-sel)' : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                      <input type="checkbox" checked={selectedFiles.has(f.FileName)} onChange={() => toggleFile(f.FileName)} style={{ accentColor: 'var(--jm-link)', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <strong>{friendlyFileName(f.FileName)}</strong>
                        <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>{f.FileName}</span>
                      </span>
                    </label>
                  ))}
                  {hiddenCount > 0 && (
                    <button type="button" onClick={() => setFilesExpanded((v) => !v)}
                      style={{ background: 'none', border: '1px dashed var(--jm-line)', borderRadius: 6, color: 'var(--jm-link)', cursor: 'pointer', padding: '6px 10px', fontSize: 12, textAlign: 'center', marginTop: 2 }}>
                      {filesExpanded ? '▲ Collapse' : `▼ Show ${hiddenCount} more`}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4, borderTop: '1px solid var(--jm-line)', paddingTop: 12 }}>
                  {printDestination === 'mfiles' && selectedFiles.size > 4 && (
                    <div style={{ fontSize: 11, padding: '6px 10px', borderRadius: 6, backgroundColor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', color: 'var(--jm-warn)', marginBottom: 8, width: '100%' }}>
                      ⚠ Saving {selectedFiles.size} files to M-Files may take several minutes. Keep this window open until complete.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn" type="button" disabled={selectedFiles.size === 0}
                    onClick={printDestination === 'mfiles' ? handleSaveToMFiles : handleDownloadSelected}
                    style={{ flex: 1 }}>
                    <FileDown size={15} />
                    {printDestination === 'mfiles'
                      ? `Save ${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''} to M-Files`
                      : `Download ${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''}`}
                  </button>
                  <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Cancel</button>
                </div>
              </>);
            })()}

            {/* downloading */}
            {printPhase === 'downloading' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Downloading…</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {printFiles.filter((f) => selectedFiles.has(f.FileName)).map((f) => {
                  const status = downloadProgress[f.FileName] || 'pending';
                  return (
                    <div key={f.FileName} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                      {status === 'pending' && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />}
                      {status === 'done'    && <span style={{ color: 'var(--jm-good)', flexShrink: 0 }}>✓</span>}
                      {status === 'error'   && <span style={{ color: 'var(--jm-bad)', flexShrink: 0 }}>✗</span>}
                      <span style={{ opacity: status === 'pending' ? 0.5 : 1 }}>{friendlyFileName(f.FileName)}</span>
                    </div>
                  );
                })}
              </div>
            </>)}

            {/* done */}
            {printPhase === 'done' && (<>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Downloads Complete</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {printFiles.filter((f) => selectedFiles.has(f.FileName)).map((f) => {
                  const status = downloadProgress[f.FileName];
                  return (
                    <div key={f.FileName} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ color: status === 'done' ? 'var(--jm-good)' : 'var(--jm-bad)', flexShrink: 0 }}>{status === 'done' ? '✓' : '✗'}</span>
                      <span>{friendlyFileName(f.FileName)}</span>
                      {status === 'error' && <span className="muted" style={{ fontSize: 11 }}>— failed</span>}
                    </div>
                  );
                })}
              </div>
              <button className="btn" type="button" onClick={closePrintModal} style={{ marginTop: 4 }}>Close</button>
            </>)}

            {/* error */}
            {printPhase === 'error' && (<>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--jm-bad)' }}>Error</div>
              <div style={{ fontSize: 13, color: 'var(--jm-dim)', wordBreak: 'break-word' }}>{printError}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn" type="button" onClick={() => setPrintPhase('pick-type')}>Try Again</button>
                <button className="btn" type="button" onClick={closePrintModal} style={{ opacity: 0.6 }}>Close</button>
              </div>
            </>)}
          </div>
        </div>
      )}

      {/* ── Job Card ─────────────────────────────────────────────────────── */}
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760, display: 'flex', alignItems: 'center', gap: 8 }}>
                {job?.jobLink ? (
                  <a href={job.jobLink} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#1a3a5c', border: '1px solid #7eb8f755', color: '#7eb8f7', transition: 'border-color 0.15s, background-color 0.15s', width: '100%', boxSizing: 'border-box' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1e4570'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#7eb8f7'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1a3a5c'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#7eb8f755'; }}>
                    <span>{job.Job_Name || (jobLoading ? 'Loading…' : 'Job not found')}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, width: '100%', boxSizing: 'border-box' }}>
                    {job?.Job_Name || (jobLoading ? 'Loading…' : 'Job not found')}
                  </span>
                )}
                {job && missingAlert(job) && (
                  <span title={missingAlert(job) ?? undefined}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', backgroundColor: '#422006', border: '1px solid #fbbf24', color: '#fbbf24', flexShrink: 0, cursor: 'default' }}>
                    <TriangleAlert size={13} />
                  </span>
                )}
                {job && isVip(job) && (
                  <span title="Client >$20k in Past Year"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', backgroundColor: '#0f2444', border: '1px solid #7eb8f7', color: '#7eb8f7', flexShrink: 0, cursor: 'default' }}>
                    <Crown size={13} />
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                {job?.clientLink ? (
                  <a href={job.clientLink} target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#2d1f5e', border: '1px solid #a78bfa55', color: '#a78bfa', transition: 'border-color 0.15s, background-color 0.15s', width: '100%', boxSizing: 'border-box' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#3a2870'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#a78bfa'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#2d1f5e'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#a78bfa55'; }}>
                    <span>{job?.ClientCode || '—'} · {job?.ClientName || '—'}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 6, fontSize: 15, fontWeight: 700, backgroundColor: '#2d1f5e', border: '1px solid #a78bfa55', color: '#a78bfa', width: '100%', boxSizing: 'border-box' }}>
                    {job?.ClientCode || '—'} · {job?.ClientName || '—'}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
              {job && (
                <button className="btn" type="button" onClick={openPrintModal} title={hasReturnType ? 'Print Return' : 'No return type — print unavailable'}
                  disabled={!hasReturnType}
                  style={{ position: 'relative', animation: hasReturnType ? 'printPulse 3s ease-in-out infinite' : 'none' }}>
                  <style>{`@keyframes printPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(126,184,247,0); border-color: rgba(126,184,247,0.3); } 50% { box-shadow: 0 0 8px 2px rgba(126,184,247,0.25); border-color: rgba(126,184,247,0.85); } }`}</style>
                  <FileDown size={16} /> Print Return
                </button>
              )}
              {job && (
                <button type="button" onClick={handleKnuulaClick} disabled={knuulaLoading} title="Open Knuula contract"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.7)', border: '1px solid rgba(167,139,250,0.4)', cursor: knuulaLoading ? 'wait' : 'pointer', transition: 'border-color 0.15s, background-color 0.15s', opacity: knuulaLoading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!knuulaLoading) { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.15)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(167,139,250,0.8)'; }}}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.7)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(167,139,250,0.4)'; }}>
                  {knuulaLoading
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <img src="https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/KnuulaLogo.png" alt="Knuula" style={{ height: 16, width: 'auto', display: 'block' }} />}
                </button>
              )}
              <span className="pill">Due: {job?.CurrentDueDate ? new Date(job.CurrentDueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}</span>
              <span className="pill">Status: {job?.ReturnStatusFed || job?.JobWorkStatus || '—'}</span>
            </div>
          </div>
        </div>

        <div className="bd">
          <div className="kv">
            {fields.map(([k, v]) => (
              <div key={k} className="field">
                <div className="k">{k}</div>
                <div className="v">{v}</div>
              </div>
            ))}
          </div>

          {/* ── Fixed assembly / shipping row ──────────────────────────── */}
          {job && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 10,
                marginTop: 10,
              }}>
                {([
                  ['Assembly Instructions',          asmShip.assemblyInstructions,       true],
                  ['Additional Comments (Assembly)', asmShip.additionalCommentsAssembly, true],
                  ['Shipping Instructions',         asmShip.shippingInstructions,       false],
                  ['Additional Comments (Shipping)', asmShip.additionalCommentsShipping, false],
                ] as [string, string, boolean][]).map(([label, value, isPurple]) => (
                  <div key={label} className="field" style={{
                    backgroundColor: isPurple
                      ? 'rgba(167,139,250,0.08)'
                      : 'rgba(126,184,247,0.08)',
                    border: `1px solid ${isPurple
                      ? 'rgba(167,139,250,0.2)'
                      : 'rgba(126,184,247,0.2)'}`,
                    borderRadius: 6,
                    padding: '8px 12px',
                  }}>
                    <div className="k" style={{ color: isPurple ? '#c4b5fd' : '#93c5fd' }}>
                      {label}
                    </div>
                    <div className="v">{value}</div>
                  </div>
                ))}
              </div>

              {/* Extra fields not in the fixed four */}
              {asmShip.extra.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                  marginTop: 10,
                }}>
                  {asmShip.extra.map(([label, value]) => (
                    <div key={label} className="field" style={{
                      borderRadius: 6,
                      padding: '8px 12px',
                    }}>
                      <div className="k">{label}</div>
                      <div className="v">{value}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Return Summary ────────────────────────────────────────────────── */}
      {hasReturnType && (
        <section className="card">
          <div className="hd">
            <div className="row">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontWeight: 760 }}>Return Summary</div>
                <div className="muted" style={{ fontSize: 13 }}>Fetched as raw XML from our API (export batch → zip → XML)</div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span className="badge">Taxpayer: {meta.header.TaxpayerName || '—'}</span>
                <span className="badge">Tax Year: {meta.header.TaxYear || '—'}</span>
                <span className="badge">EIN/SSN: {meta.header.EINorSSN || '—'}</span>
                <span className="badge">ClientID: {meta.header.ClientID || '—'}</span>
                <span className="badge">Returns: {meta.returnCount}</span>
              </div>
            </div>
          </div>
          <div className="bd">
            {inUseStatus?.inUse ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderRadius: 8, backgroundColor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', fontSize: 14 }}>
                <TriangleAlert size={16} style={{ flexShrink: 0 }} />
                <span>The return is currently in use by <strong>{inUseStatus.lastUpdatedBy || 'another user'}</strong>. Please try again when they have closed the return.</span>
              </div>
            ) : decodedReturnId
              ? <ReturnSummaryViewer returnId={decodedReturnId} onMeta={setMeta} />
              : <div className="muted">Missing returnId.</div>}
          </div>
        </section>
      )}
    </div>
  );
}